/**
 * useMidiController — Web MIDI API hook for DJ-style controllers.
 *
 * Supports any Class-Compliant MIDI controller (Pioneer DDJ, Numark, Hercules,
 * Native Instruments, APC, Launch Control, etc.). Bindings are per-control
 * (button or continuous) and persisted to localStorage under a given key.
 *
 * Usage:
 *   const midi = useMidiController({ storageKey: "tapin_dj_midi" });
 *   midi.bindHandler("deck-a-play", () => setPlayA(v => !v));           // button
 *   midi.bindHandler("xfader", (v) => setXfader(v));                    // continuous 0..1
 *   <button onClick={() => midi.startLearn("deck-a-play")}>Learn</button>
 *
 * Binding record shape:
 *   { kind: "note"|"cc", channel: 0-15, number: 0-127 }
 */
import { useEffect, useRef, useState, useCallback } from "react";

const MSG_NOTE_ON  = 0x90;
const MSG_NOTE_OFF = 0x80;
const MSG_CC       = 0xB0;

function safeLoad(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function safeSave(key, obj) {
  try { localStorage.setItem(key, JSON.stringify(obj)); } catch {}
}

export default function useMidiController({ storageKey = "tapin_dj_midi" } = {}) {
  const [access, setAccess] = useState(null);
  const [devices, setDevices] = useState([]);          // [{id, name, manufacturer}]
  const [mappings, setMappings] = useState(() => safeLoad(storageKey));
  const [learningFor, setLearningFor] = useState(null); // controlId
  const [lastMsg, setLastMsg] = useState(null);         // {kind, channel, number, value}
  const [supported, setSupported] = useState(
    typeof navigator !== "undefined" && !!navigator.requestMIDIAccess
  );

  const handlersRef = useRef({});     // { controlId: fn(value) }
  const mappingsRef = useRef(mappings);
  const learningRef = useRef(null);
  useEffect(() => { mappingsRef.current = mappings; safeSave(storageKey, mappings); }, [mappings, storageKey]);
  useEffect(() => { learningRef.current = learningFor; }, [learningFor]);

  const bindHandler = useCallback((controlId, fn) => {
    handlersRef.current[controlId] = fn;
  }, []);

  const unbindHandler = useCallback((controlId) => {
    delete handlersRef.current[controlId];
  }, []);

  const startLearn = useCallback((controlId) => setLearningFor(controlId), []);
  const cancelLearn = useCallback(() => setLearningFor(null), []);

  const clearMapping = useCallback((controlId) => {
    setMappings((m) => {
      const n = { ...m };
      delete n[controlId];
      return n;
    });
  }, []);

  const refreshDevices = useCallback((a) => {
    if (!a) return;
    const list = [];
    a.inputs.forEach((inp) => list.push({ id: inp.id, name: inp.name || "MIDI Input", manufacturer: inp.manufacturer || "" }));
    setDevices(list);
  }, []);

  // Handle incoming MIDI messages
  const onMessage = useCallback((e) => {
    const [status, data1, data2 = 0] = e.data;
    const type = status & 0xF0;
    const channel = status & 0x0F;
    let kind = null;
    let value = 0;
    if (type === MSG_CC) {
      kind = "cc";
      value = data2 / 127;
    } else if (type === MSG_NOTE_ON && data2 > 0) {
      kind = "note";
      value = 1;
    } else if (type === MSG_NOTE_OFF || (type === MSG_NOTE_ON && data2 === 0)) {
      kind = "note";
      value = 0;
    } else {
      return;
    }

    const msg = { kind, channel, number: data1, value, raw: Array.from(e.data) };
    setLastMsg(msg);

    // Learn mode — bind next message (only fire on press-down for notes)
    const learnId = learningRef.current;
    if (learnId && (kind === "cc" || (kind === "note" && value === 1))) {
      setMappings((m) => ({ ...m, [learnId]: { kind, channel, number: data1 } }));
      setLearningFor(null);
      return;
    }

    // Dispatch to bound handler
    const map = mappingsRef.current;
    for (const [controlId, binding] of Object.entries(map)) {
      if (
        binding &&
        binding.kind === kind &&
        binding.channel === channel &&
        binding.number === data1
      ) {
        const fn = handlersRef.current[controlId];
        if (fn) {
          // Notes: pass 1 on press, 0 on release (handler can toggle on press).
          // CCs: pass normalized 0..1.
          try { fn(value, msg); } catch {}
        }
      }
    }
  }, []);

  // Request + wire up MIDI access
  const enable = useCallback(async () => {
    if (!navigator.requestMIDIAccess) { setSupported(false); return false; }
    try {
      const a = await navigator.requestMIDIAccess({ sysex: false });
      setAccess(a);
      refreshDevices(a);
      a.inputs.forEach((inp) => { inp.onmidimessage = onMessage; });
      a.onstatechange = () => {
        refreshDevices(a);
        a.inputs.forEach((inp) => { if (!inp.onmidimessage) inp.onmidimessage = onMessage; });
      };
      return true;
    } catch (e) {
      setSupported(false);
      return false;
    }
  }, [onMessage, refreshDevices]);

  // Auto-attempt on mount — many browsers grant without prompt for non-sysex
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const a = await navigator.requestMIDIAccess({ sysex: false });
        if (cancelled) return;
        setAccess(a);
        refreshDevices(a);
        a.inputs.forEach((inp) => { inp.onmidimessage = onMessage; });
        a.onstatechange = () => {
          refreshDevices(a);
          a.inputs.forEach((inp) => { if (!inp.onmidimessage) inp.onmidimessage = onMessage; });
        };
      } catch { /* user may need to call enable() via button click */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  return {
    supported,
    enabled: !!access,
    devices,
    mappings,
    lastMsg,
    learningFor,
    enable,
    bindHandler,
    unbindHandler,
    startLearn,
    cancelLearn,
    clearMapping,
  };
}
